import React, { useCallback, useEffect, useState } from 'react';
import { Button, Drawer, Popconfirm, Row, Space, Table, Typography, Card, Tooltip, Tag, Input, Select, Switch } from 'antd';
import type { TableProps } from 'antd';
import type { FilterValue, SortOrder, SorterResult, TablePaginationConfig } from 'antd/es/table/interface';
import { SearchOutlined } from '@ant-design/icons';
import { openNotification } from '../../../utils';
import { apiFetch } from 'services/apiClient';
import { debounce } from 'lodash';
import TrashIcon from 'assets/icons/trash.svg';
import PenIcon from 'assets/icons/pen.svg';

import { listMeasuringInstruments, deleteMeasuringInstrument, updateMeasuringInstrument } from '../services/measuring-instrumentService';
import MeasuringInstrumentForm from './Form';

export default function MeasuringInstrumentList({ referenceId }: { referenceId?: number }){
  const [data, setData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | undefined>();
  // Filters
  const [filterOpen, setFilterOpen] = useState(false);
  const [attrOptions, setAttrOptions] = useState<any[]>([]);
  // committed filters
  const [selectedAttrIds, setSelectedAttrIds] = useState<number[]>([]);
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | undefined>(true);
  // pending filters
  const [pendingAttrIds, setPendingAttrIds] = useState<number[]>([]);
  const [pendingIsActive, setPendingIsActive] = useState<boolean | undefined>(true);
  // Search
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const debouncedApplySearch = React.useMemo(() => debounce((val: string) => { setSearch(val); }, 300), []);
  React.useEffect(() => () => { debouncedApplySearch.cancel(); }, [debouncedApplySearch]);
  

  const DEFAULT_SORT_COLUMN = 'name';
  const DEFAULT_SORT_ORDER: SortOrder = 'ascend';
  const [sortState, setSortState] = useState<{ columnKey?: string; order?: SortOrder }>({
    columnKey: DEFAULT_SORT_COLUMN,
    order: DEFAULT_SORT_ORDER
  });
  const resolveSortParam = () => {
    if (!sortState.columnKey || !sortState.order) return `${DEFAULT_SORT_COLUMN},asc`;
    const direction = sortState.order === 'descend' ? 'desc' : 'asc';
    return `${sortState.columnKey},${direction}`;
  };
  const fetchData = useCallback(async () => {
    const filters: string[] = [];
    if (selectedAttrIds.length) filters.push('attributeId.in=' + selectedAttrIds.join(','));
    if (typeof isActiveFilter === 'boolean') filters.push('isActive.equals=' + isActiveFilter);
    if (search && search.length >= 2) filters.push('name.contains=' + encodeURIComponent(search));
    const query = filters.join('&');
    const sortParam = resolveSortParam();
    const res = await listMeasuringInstruments(page-1, pageSize, sortParam, query);
    setData(res.content);
    setTotal(res.totalElements);
  }, [page, pageSize, selectedAttrIds, isActiveFilter, search, sortState.columnKey, sortState.order]);
  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { apiFetch('/attributes?isActive=true').then(setAttrOptions); }, []);
  const openCreate = () => { setEditingId(undefined); setOpen(true); };
  const openEdit = (id:number) => { setEditingId(id); setOpen(true); };
  const handleClose = () => setOpen(false);
  const openFilter = () => { setPendingAttrIds(selectedAttrIds); setPendingIsActive(isActiveFilter); setFilterOpen(true); };
  const closeFilter = () => setFilterOpen(false);
  const clearFilter = () => { setPendingAttrIds([]); setPendingIsActive(undefined); };
  const handleDelete = async (id:number) => {
    try {
      await deleteMeasuringInstrument(id);
      openNotification('success', 'Başarılı', 'Ölçüm Cihazı Silindi');
      fetchData();
    } catch (e) {
      openNotification('error', 'Hata', 'Ölçüm Cihazı silme işlemi başarısız');
    }
  };
  const buildUpdatePayload = (row: any, isActiveVal: boolean) => {
    const toId = (val: any) => (typeof val === 'object' && val !== null ? val.id : val);
    const attrIds = Array.isArray(row?.attributes)
      ? row.attributes.map((attr: any) => toId(attr)).filter((id: any) => id != null)
      : [];
    return {
      name: row?.name ?? '',
      isActive: isActiveVal,
      attributes: attrIds,
    };
  };
  const activeSortKey = sortState.columnKey;
  const activeSortOrder = sortState.order;
  const resolveSortOrder = (key: string) => (activeSortKey === key ? activeSortOrder : undefined);
  const columns = [
    { title: 'Cihaz Adı', dataIndex: 'name', key: 'name', sorter: true, sortOrder: resolveSortOrder('name') },
    { title: 'Parametre Sayısı', dataIndex: 'attributeCount', sorter: false, render: (val: number, r: any) => {
      if (typeof val === 'number') return val;
      const attrs: any[] = Array.isArray(r?.attributes) ? r.attributes : [];
      return attrs.length;
    } },
    { title: 'Parametreler', dataIndex: 'attributes', sorter: false, render: (_: any, r: any) => {
      const attrs: any[] = Array.isArray(r?.attributes) ? r.attributes : [];
      const names = attrs
        .map((a: any) => (typeof a === 'object' ? a?.name : a))
        .filter((n: any) => !!n)
        .map((n: any) => String(n));
      if (!names.length) return '';
      const sorted = names.slice().sort((a: string, b: string) => a.localeCompare(b, 'tr'));
      return (
        <Space size={4} wrap>
          {sorted.map((n: string) => (
            <Tag key={n}>{n}</Tag>
          ))}
        </Space>
      );
    } },
    { title: 'Envanter Sayısı', dataIndex: 'totalInventoryCount',
      width: 160,
      sorter: false, render: (_: any, r: any) => {
      const total = typeof r?.totalInventoryCount === 'number' ? r.totalInventoryCount : 0;
      const active = typeof r?.activeInventoryCount === 'number' ? r.activeInventoryCount : 0;
      return <><b>Toplam: </b>{total}<br/> <b>Aktif: </b> {active}</>;
    } },
    { title: 'Durum', dataIndex: 'isActive', key: 'isActive', sorter: true, sortOrder: resolveSortOrder('isActive'), render: (_: boolean, r: any) => (
      <Switch
        checkedChildren='Aktif'
        unCheckedChildren='Pasif'
        checked={!!r?.isActive}
        onChange={async (val) => {
          try {
            const payload = buildUpdatePayload(r, val);
            await updateMeasuringInstrument(r.id, payload);
            setData(prev => prev.map(item => item.id === r.id ? ({ ...item, isActive: val }) : item));
            openNotification('success', 'Başarılı', val ? 'Ölçüm cihazı aktif yapıldı' : 'Ölçüm cihazı pasif yapıldı');
            fetchData();
          } catch (e) {
            openNotification('error', 'Hata', 'Durum güncellenemedi.');
          }
        }}
      />
    ) },
    { title: 'İşlemler', render: (_:any, r:any) => (
      <Space>
        <Tooltip title='Düzenle'>
          <Button
            icon={<img src={PenIcon} alt="pen" className="icon" />}
            shape="circle"
            onClick={() => openEdit(r.id)}
            className="icon-button"
          />
        </Tooltip>
        <Popconfirm title='Sil?' onConfirm={() => handleDelete(r.id)}>
          <Tooltip title='Sil'>
            <Button
              icon={<img src={TrashIcon} alt="trash" className="icon" />}
              shape="circle"
              className="icon-button ml-12px"
            />
          </Tooltip>
        </Popconfirm>
      </Space>
    ) }
  ];
  const handleTableChange: TableProps<any>['onChange'] = (
    _pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<any> | SorterResult<any>[]
  ) => {
    const normalizedSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const hasOrder = !!normalizedSorter?.order;
    const nextState = hasOrder
      ? {
          columnKey: (normalizedSorter?.columnKey ?? normalizedSorter?.field) as string | undefined,
          order: normalizedSorter?.order as SortOrder,
        }
      : { columnKey: DEFAULT_SORT_COLUMN, order: DEFAULT_SORT_ORDER };
    if (nextState.columnKey !== sortState.columnKey || nextState.order !== sortState.order) {
      setSortState(nextState);
      setPage(1);
    }
  };
  return (
    <>
      
      <Card
        className='br-30px kontrolx-card'
        title={(
          <Row align='bottom'>
            <Typography.Title level={4} style={{ margin: 0 }}>Ölçüm Cihazı</Typography.Title>
            <Typography.Text type='secondary' className='ms-5' style={{ fontWeight: 500 }}>
              <strong>({total})</strong> kayıt listelendi
            </Typography.Text>
          </Row>
        )}
        extra={
          <Space>
            <Input
              allowClear
              placeholder="Ara"
              prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
              className="d-input d-input-form d-input-filter"
              style={{ width: 200 }}
              value={searchInput}
              onChange={(e) => {
                const val = e.target.value || '';
                setSearchInput(val);
                if (val.length >= 2) {
                  debouncedApplySearch(val);
                } else if (val.length === 0) {
                  debouncedApplySearch('');
                }
              }}
            />
            <Button type='primary' onClick={openFilter}>Filtrele</Button>
            <Button type='primary' onClick={openCreate}>Ekle</Button>
          </Space>
        }
      >
        <Table
          rowKey='id'
          dataSource={data}
          columns={columns}
          onChange={handleTableChange}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['50', '100', '200', '500'],
            onChange: (p, s) => { setPage(p); setPageSize(s); }
          }}
        />
      </Card>
      <Drawer open={open} onClose={handleClose} destroyOnClose width={480} title={editingId ? 'Ölçüm Cihazı Düzenle' : 'Ölçüm Cihazı Oluştur'}>
        <MeasuringInstrumentForm id={editingId} onSuccess={() => { handleClose(); fetchData();}} />
      </Drawer>
      <Drawer open={filterOpen} onClose={closeFilter} destroyOnClose width={420} title='Filtre'>
        <Space direction='vertical' style={{ width: '100%' }} size='large'>
          <div>
            <Typography.Text>Parametre</Typography.Text>
            <Select
              mode='multiple'
              allowClear
              showSearch
              placeholder='Seçiniz'
              style={{ width: '100%', marginTop: 8 }}
              value={pendingAttrIds}
              onChange={(vals: number[]) => setPendingAttrIds(vals)}
              optionFilterProp='children'
              filterOption={(input, option) => ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))}
            >
              {attrOptions.filter((o: any) => o?.isActive === true).map((o: any) => (
                <Select.Option key={o.id} value={o.id}>{o.name}</Select.Option>
              ))}
            </Select>
          </div>
          <div>
            <Typography.Text>Durum</Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              value={pendingIsActive === undefined ? 'all' : pendingIsActive ? 'true' : 'false'}
              onChange={(val: 'all' | 'true' | 'false') => {
                if (val === 'all') setPendingIsActive(undefined);
                else if (val === 'true') setPendingIsActive(true);
                else setPendingIsActive(false);
              }}
              options={[
                { value: 'all', label: 'Tümü' },
                { value: 'true', label: 'Aktif' },
                { value: 'false', label: 'Pasif' },
              ]}
            />
          </div>
          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={clearFilter}>Temizle</Button>
            <Button type='primary' onClick={() => { setSelectedAttrIds(pendingAttrIds); setIsActiveFilter(pendingIsActive); closeFilter(); }}>Filtrele</Button>
          </Space>
        </Space>
      </Drawer>
    </>
  );
}
